import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Check, X, Megaphone, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact shoutout inbox for the DJ Console — renders below the songs inbox.
 * Polls every 5s. Approve marks a shoutout 'approved' and pins it to the
 * displayed "Now Shouting" banner on the DJ Console.
 */
const ShoutoutsInbox = ({ onApprove }) => {
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const pollRef = useRef(null);

  const fetchSession = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/requests/sessions/active`);
      setSession(r.data || null);
      return r.data;
    } catch {
      setSession(null);
      return null;
    }
  }, []);

  const fetchItems = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await axios.get(`${API}/requests/sessions/${sid}/shoutouts`);
      setItems(r.data || []);
    } catch (fetchErr) {
      console.warn('[ShoutoutsInbox] fetch failed:', fetchErr?.response?.status, fetchErr?.message || fetchErr);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const s = await fetchSession();
      if (s?.id) await fetchItems(s.id);
    })();
  }, [fetchSession, fetchItems]);

  useEffect(() => {
    if (!session?.id) return undefined;
    pollRef.current = setInterval(() => fetchItems(session.id), 5000);
    return () => clearInterval(pollRef.current);
  }, [session?.id, fetchItems]);

  const pending = items.filter((i) => i.status === 'pending');

  const handleApprove = async (item) => {
    setBusyId(item.id);
    try {
      await axios.put(`${API}/shoutouts/${item.id}/approve`);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: 'approved' } : i)));
      onApprove?.(item);
      toast.success('Shoutout approved');
    } catch {
      toast.error('Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (item) => {
    setBusyId(item.id);
    try {
      await axios.put(`${API}/shoutouts/${item.id}/reject`);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: 'rejected' } : i)));
    } catch {
      toast.error('Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  if (!session) return null;
  if (session?.shoutouts_enabled === false) return null;

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-3 lg:p-4 backdrop-blur-xl"
      data-testid="dj-shoutouts-inbox"
    >
      <div className="flex items-center justify-between mb-2 lg:mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 lg:w-5 lg:h-5 text-[#39FF14]" />
          <h3 className="text-xs lg:text-sm font-extrabold text-white uppercase tracking-[0.18em] font-['Outfit']">
            Shoutouts
          </h3>
          {pending.length > 0 && (
            <span className="bg-[#39FF14] text-black text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="shoutout-pending-count">
              {pending.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-gray-400 hover:text-white p-1 rounded transition-colors"
          data-testid="shoutout-toggle-collapse"
          aria-label={collapsed ? 'Expand shoutouts' : 'Collapse shoutouts'}
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        pending.length === 0 ? (
          <div className="flex items-center gap-3 py-2 lg:py-3 text-gray-500 text-xs lg:text-sm">
            <Megaphone className="w-4 h-4" />
            No new shoutouts.
          </div>
        ) : (
          <div className="flex gap-2 lg:gap-3 overflow-x-auto pb-1 -mx-1 px-1 hide-scrollbar">
            {pending.map((item) => (
              <div
                key={item.id}
                className="flex flex-col gap-2 bg-black/40 border border-[#39FF14]/20 rounded-xl p-3 flex-shrink-0 w-[280px] lg:w-[340px]"
                data-testid={`shoutout-card-${item.id}`}
              >
                <p className="text-[10px] text-[#39FF14] font-semibold uppercase tracking-wider truncate">
                  {item.guest_name}
                </p>
                <p className="text-white text-sm leading-snug line-clamp-3">{item.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    onClick={() => handleApprove(item)}
                    disabled={busyId === item.id}
                    className="flex-1 bg-[#39FF14] hover:bg-[#2dd60f] text-black h-7 rounded-full text-[11px] font-bold tracking-wider"
                    data-testid={`shoutout-approve-${item.id}`}
                  >
                    <Check className="w-3 h-3 mr-1" /> APPROVE
                  </Button>
                  <Button
                    onClick={() => handleReject(item)}
                    disabled={busyId === item.id}
                    variant="ghost"
                    className="text-[#FF3B3B] hover:bg-[#FF3B3B]/10 h-7 w-7 p-0 rounded-full"
                    title="Reject"
                    data-testid={`shoutout-reject-${item.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default ShoutoutsInbox;
